/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-25 17:45:06
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Card, Button, TextStyle, Link } from '@shopify/polaris';
import type { FormProps } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import MainMenu from 'src/components/containers/main-menu';
import EditModal from './modal';

import { URL } from 'src/constants/links';
import DidYouKnow from 'src/components/views/custom-range-slider/did-you-know';
import ListCustomRangeSlider from 'src/components/views/custom-range-slider/list-custom-range-sliders';
import ProcessingUpdateBanner from 'src/components/views/common/processing-update-banner';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import YoutubeModal from 'src/components/views/common/youtube-modal';

import * as FilterDataActions from 'actions/filter-data-actions';
import * as customRangeSliderActions from 'src/stores/actions/custom-range-slider-actions';
import TitleHelp from 'src/components/views/common/title-help';

type Props = {
  getCustomRangeSliders: () => void,
  updateCustomRangeSlider: () => void,
  deleteCustomRangeSlider: () => void
};

type ComposedProps = Props & WithI18nProps & FormProps;

type State = {
  selectedId: number,
  activeModalEdit: boolean,
  youtubeModal: boolean
};

class CustomRangeSliders extends Component<ComposedProps, State> {
  state = {
    selectedId: -1,
    activeModalEdit: false,
    youtubeModal: false
  };

  componentDidMount() {
    const { getCustomRangeSliders, getFilterData } = this.props;

    getFilterData();
    getCustomRangeSliders();
  }

  componentWillReceiveProps(nextProps) {
    const { saving, deleting, scheduleSyncIn, waitSyncAt } = nextProps;
    const {
      saving: savingPrev,
      deleting: deletingPrev,
      getSyncProcessStatus,
      updateShowBanner
    } = this.props;

    // Check to show banner Processing update when change status/ import
    if ((savingPrev && !saving) || (deletingPrev && !deleting)) {
      if (waitSyncAt) {
        // Time from now to sync point
        let syncDuration = waitSyncAt - Math.floor(Date.now() / 1000);
        syncDuration = syncDuration > 0 ? syncDuration : 0;
        // Time to finish sync point
        syncDuration = Math.floor(syncDuration / 60) + scheduleSyncIn;

        updateShowBanner(syncDuration, true);
      }
      // Call sync process status
      getSyncProcessStatus();
    }
  }

  render() {
    const {
      i18n,
      loading,
      deleting,
      success,
      syncDuration,
      showScheduleSync,
      customRangeSliders,
      numberOfRangeSliders,
      options
    } = this.props;
    const { youtubeModal } = this.state;

    // Check having customRangeSlider
    const hasCustomRangeSlider = !_.isEmpty(customRangeSliders);

    return (
      <div className="BC-Page BC-Page__CustomRangeSlider">
        <MainMenu menuId="menu-filters-custom-range-sliders" />
        <Page title={i18n.translate('AdminHTML.CustomRangeSlider.CustomRangeSlider.title')}>
          <TitleHelp
            i18n={i18n}
            onPlay={this.handleOpenYoutubeModal}
            hrefUserManual={URL['Link_V2_Filter_CustomRangeSlider_ManualGuide']}
          />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Installation reminder status banner */}
                <InstallationRemindStatusBanner />

                {/* Did you know? */}
                <DidYouKnow numberOfRangeSliders={numberOfRangeSliders} />

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />

                {/* Processing update banner */}
                {showScheduleSync ? (
                  <ProcessingUpdateBanner
                    syncDuration={syncDuration}
                    featureName="customRangeSlider"
                  />
                ) : (
                  ''
                )}
              </div>
            </Layout.Section>

            {/* List of customRangeSlider */}
            <Layout.Section>
              <Card sectioned>
                <div>
                  <ListCustomRangeSlider
                    key={'list-customRangeSliders'}
                    loading={loading}
                    list={customRangeSliders}
                    options={options}
                    deleteCustomRangeSlider={this.deleteCustomRangeSlider}
                    deleting={deleting}
                    success={success}
                    handleActiveModalEdit={this.handleActiveModalEdit}
                  />

                  {hasCustomRangeSlider && customRangeSliders.length < numberOfRangeSliders ? (
                    <div className="addNew">
                      <Button plain onClick={() => this.handleActiveModalEdit()}>
                        {i18n.translate('AdminHTML.CustomRangeSlider.CustomRangeSlider.addNew')}
                      </Button>
                    </div>
                  ) : (
                    ''
                  )}
                </div>
              </Card>

              {customRangeSliders.length >= numberOfRangeSliders ? (
                <div className="BC-Page BC-Page__Bottom-Text">
                  <TextStyle>
                    {i18n.translate('AdminHTML.CustomRangeSlider.CustomRangeSlider.bottomText', {
                      nt_number: numberOfRangeSliders,
                      t: (
                        <Link external url={URL.CustomRangeSliderDoc}>
                          {i18n.translate(
                            'AdminHTML.CustomRangeSlider.CustomRangeSlider.bottomText_t'
                          )}
                        </Link>
                      )
                    })}
                  </TextStyle>
                </div>
              ) : (
                ''
              )}
            </Layout.Section>
          </Layout>

          {/* Create/Edit modal */}
          <EditModal
            active={this.state.activeModalEdit}
            handleChange={this.handleActiveModalEdit}
            customRangeSlider={
              _.find(customRangeSliders, item => item.id === this.state.selectedId) || {}
            }
          />
          <YoutubeModal
            active={youtubeModal}
            embeddedURL={URL['Link_V2_Filter_CustomRangeSlider_Video']}
            title={i18n.translate(
              'AdminHTML.CustomRangeSlider.CustomRangeSlider.youtubeModal_Title'
            )}
            onClose={this.handleCloseYoutubeModal}
          />
        </Page>
      </div>
    );
  }

  deleteCustomRangeSlider = slider => {
    const { deleteCustomRangeSlider } = this.props;

    deleteCustomRangeSlider(slider);
  };

  handleActiveModalEdit = id => {
    const { activeModalEdit } = this.state;

    if (activeModalEdit) {
      this.setState({ activeModalEdit: !activeModalEdit, selectedId: -1 });
    } else {
      this.setState({ activeModalEdit: !activeModalEdit, selectedId: id });
    }
  };

  handleOpenYoutubeModal = () => this.setState({ youtubeModal: true });
  handleCloseYoutubeModal = () => this.setState({ youtubeModal: false });
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterData: () => dispatch(FilterDataActions.getFilterData(ownProps.i18n)),
  getCustomRangeSliders: () =>
    dispatch(customRangeSliderActions.getCustomRangeSliders(ownProps.i18n)),
  deleteCustomRangeSlider: customRangeSlider =>
    dispatch(customRangeSliderActions.deleteCustomRangeSlider(customRangeSlider, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  let numberOfRangeSliders = _.get(state.oauth.tenantApp, 'numberOfRangeSliders', 12);
  // Make sure number of range slide is 12
  // Why? Field numberOfRangeSliders is used to determine the number of filter option Range Slide in filter tree, this might be less than 12
  // After release Custom Range Slider feature, it isn't used until this update
  numberOfRangeSliders = numberOfRangeSliders < 12 ? 12 : numberOfRangeSliders;

  const options = _.get(state.filterData.filterData, 'optionsList', []);

  return {
    options,
    customRangeSliders: _.get(state.customRangeSlider, 'customRangeSliders', []),
    loading: _.get(state.customRangeSlider, 'loading', false),
    saving: _.get(state.customRangeSlider, 'saving', false),
    deleting: _.get(state.customRangeSlider, 'deleting', false),
    success: _.get(state.customRangeSlider, 'success', false),
    scheduleSyncIn: _.get(state.customRangeSlider, 'scheduleSyncIn', 0),
    waitSyncAt: _.get(state.customRangeSlider, 'waitSyncAt', 0),
    numberOfRangeSliders
  };
};

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(CustomRangeSliders)),
  'SyncStoreData'
);
